import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { message } from 'antd';
import { usePostHog } from '@posthog/react';
import { useSWRConfig } from 'swr';
import { consumeMagicLink, fetchMe, SIGNED_USER_KEY } from 'api/auth';
import { Role } from 'api/types/auth';
import { useAnalytics } from 'analytics';
import UnauthLayout from 'components/UnauthLayout';
import { URLS } from 'components/Routes/urls';
import { ERROR_MESSAGE_EXPIRE_TOKEN } from 'pages/reset-password/constants';
import { Button, Typography } from 'ui-kit';
import { getMagicLinkToken } from 'utils/magicLinkToken';
import { goToNextDestination, takeNextDestination } from 'utils/nextUrl';
import { takeSignupPending } from 'utils/signupPending';
import { getLinkRequestError } from './utils';
import './MagicLinkPage.scss';

export default function MagicLinkPage() {
  const history = useHistory();
  const posthog = usePostHog();
  const capture = useAnalytics();
  const { mutate } = useSWRConfig();
  const [token] = useState(getMagicLinkToken);
  const [isExpired, setIsExpired] = useState(!token);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    capture('magic_link_opened', { has_token: Boolean(token) });
  }, []);

  const handleContinue = () => {
    setIsSubmitting(true);

    consumeMagicLink({ token })
      .then(({ is_new_host: isNewHost, email, owner_id: ownerId }) => {
        const next = takeNextDestination();
        const isSameBrowser = takeSignupPending();

        if (isNewHost) {
          const signupProperties = {
            method: 'magic_link' as const,
            role: Role.HOST,
            has_next_param: Boolean(next),
            same_browser: isSameBrowser,
          };
          capture('signup_completed', signupProperties);
          // Kept until nothing reads it.
          capture('user_signed_up', signupProperties);

          // Here and not on the signup form: at submit nobody has proven the email, so
          // this would credit an ad click for an address that is already a Host.
          window.fbq?.(
            'trackCustom',
            'CreateAccount',
            { from: 'email', email },
            { eventID: ownerId },
          );
        }

        // The Host is signed in by now, so a failure here must not block them.
        return fetchMe()
          .then((me) => {
            posthog?.identify(String(me.user_id), { email: me.email, role: me.role });

            if (!isNewHost) {
              capture('user_logged_in', { role: me.role, method: 'magic_link' });
            }

            return mutate(SIGNED_USER_KEY, me, false);
          })
          .then(
            () => ({ next, isUserFresh: true }),
            () => ({ next, isUserFresh: false }),
          );
      })
      .then(({ next, isUserFresh }) => {
        // LoginGuard reads jwt/me/ from the SWR cache. After a Store visit the cache holds a
        // Guest, and the login page signs a Guest out with the token of the moment: the
        // Host's. With no new answer to write, a page load starts from an empty cache.
        const router = isUserFresh
          ? history
          : { push: (path: string) => window.location.assign(path) };

        if (!goToNextDestination(router, next)) {
          router.push(URLS.STORES);
        }
      })
      .catch((error) => {
        setIsSubmitting(false);

        if (error?.message === ERROR_MESSAGE_EXPIRE_TOKEN) {
          capture('magic_link_consume_failed', { reason: 'expired_or_invalid' });
          setIsExpired(true);
        } else {
          capture('magic_link_consume_failed', { reason: 'other' });
          message.error(getLinkRequestError(error).message);
        }
      });
  };

  if (isExpired) {
    return (
      <UnauthLayout compact>
        <div className="MagicLinkPage">
          <div className="MagicLinkPage-copy">
            <Typography variant="h5">This link is used or expired</Typography>
            <Typography variant="body2" color="textGray">
              A sign-in link works once and expires after 15 minutes.
            </Typography>
          </div>
          <div className="MagicLinkPage-actions">
            <Button to={URLS.MAGIC_LINK_REQUEST} variant="contained" fullWidth>
              Request a new link
            </Button>
            <Button to="/login/" variant="text" color="primary" size="small" fullWidth>
              Back to sign in
            </Button>
          </div>
        </div>
      </UnauthLayout>
    );
  }

  return (
    <UnauthLayout compact>
      <div className="MagicLinkPage">
        <Typography variant="h5">Finish signing in</Typography>
        <div className="MagicLinkPage-actions">
          <Button variant="contained" fullWidth loading={isSubmitting} onClick={handleContinue}>
            Continue
          </Button>
        </div>
      </div>
    </UnauthLayout>
  );
}
